import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ScanField } from "@/components/ScanField";
import { PhotoUploader } from "@/components/PhotoUploader";

import { useFlash } from "@/floor/useFlash";
import { FloorActionButton } from "@/floor/FloorActionButton";
import { ArrowLeft, Camera, Package } from "lucide-react";

// Floor-native Rapid Photo: scan a part (camera ON-DEMAND) → snap → save → next.
// Same endpoints as the desk flow, floor-simple shell (no desk chrome).
export default function FloorRapidPhoto() {
  const navigate = useNavigate();
  const [part, setPart] = useState(null);
  const [busy, setBusy] = useState(false);
  const { trigger, FlashOverlay } = useFlash();

  const onScan = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get("/parts/resolve-scan", { params: { code } });
      trigger("success", data.short_code || "PART"); setPart(data);
    } catch { trigger("error", "NOT A PART"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex-1 flex flex-col p-4 sm:p-6 gap-5" data-testid="floor-rapid-photo">
      {FlashOverlay}
      <button onClick={() => navigate("/floor")} className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start" data-testid="floor-back">
        <ArrowLeft className="w-5 h-5" /> Home
      </button>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl bg-[#B01E28] flex items-center justify-center"><Camera className="w-8 h-8 text-white" /></div>
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight leading-none">Rapid Photo</h1>
          <p className="text-base text-slate-500 font-medium mt-1">Scan a part, snap, next.</p>
        </div>
      </div>

      {!part ? (
        <div className="mt-2" data-testid="floor-rapid-scan">
          <ScanField testid="floor-rapid-scan" label="Scan a part tag" submitLabel="Look up" onScan={onScan} busy={busy} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border-2 border-[#E2E8F0] bg-white p-4 flex items-center gap-3" data-testid="floor-rapid-part">
            <Package className="w-8 h-8 text-slate-400" />
            <div className="min-w-0">
              <div className="font-mono text-lg font-bold">{part.short_code}</div>
              <div className="text-base text-slate-500 truncate">{part.part_type || "Part"} · {part.photo_count} photo{part.photo_count === 1 ? "" : "s"}</div>
            </div>
          </div>
          <PhotoUploader endpoint={`/parts/${part.id}/photos`} cameraFirst size="md" buttonLabel="Take photo"
            testidPrefix="floor-rapid-uploader"
            onUploaded={() => { trigger("success", "PHOTO SAVED"); }} />
          <FloorActionButton testid="floor-rapid-next" label="Next part" icon="ScanLine" tone="primary" onClick={() => setPart(null)} />
        </div>
      )}
    </div>
  );
}
